import * as React from 'react';
import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import Col from 'reactstrap/lib/Col';
import Row from 'reactstrap/lib/Row';
import {
    createProspectUser,
    RegisterProspectUserDto,
    resetPassword
} from '../../../../api/ProspectUser';
import Form from '../../../common/form/Form';
import FormValidationDisplay from '../../../common/form/ValidationDisplay';
import LoadingOverlay from '../../../common/LoadingOverlay';
import { prospectPages, prospectPath } from '../ProspectRouter';
import { DualRoutedProps, TourPage, tourSignupPath } from '../signup/TourRouter';
import ProspectSignupInputForm from './ProspectSignupInputForm';
import SetPasswordWorkflow from './SetPasswordWorkflow';

interface Props extends DualRoutedProps { }

const termsPdf = require('../../../../pdfs/Tour24_Terms_of_ Use_09.2022.pdf');

const RegistrationPage = (props: Props) => {
    const defaultUserInfo: RegisterProspectUserDto = {
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        contactPreference: 0
    };

    const [errorMessage, setErrorMessage] = useState<ReactNode | undefined>(undefined);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [wasFormValidated, setWasFormValidated] = useState<boolean>(false);
    const [userInfo, setUserInfo] = useState<RegisterProspectUserDto>(defaultUserInfo);
    const [userInfoEntered, setUserInfoEntered] = useState<boolean>(false);
    const [validData, setValidData] = useState(false);

    const propertySlug = props.match.params.propertySlug;

    const handleSubmitUserInfo = async () => {
      if(validData){
        setIsLoading(true);

        let result = await createProspectUser(userInfo);

        if (result.success) {
            setErrorMessage(undefined);
            setUserInfoEntered(true);
        } else {
            if (result.error !== undefined && result.error === 'DuplicateUserName') {
                setErrorMessage(
                    <div>
                        An account has already been created with this cell phone number. Please
                        visit the{' '}
                        <Link
                            className="username-taken-forgot-password-link"
                            to={prospectPath(prospectPages.resetPassword)}
                        >
                            Forgot Password
                        </Link>{' '}
                        page to reset your password and log in.
                    </div>
                );
            } else {
                setErrorMessage(result.error);
            }
        }

        setIsLoading(false);
      }
    };

    return (
        <Row className="prospect-auth-form">
            <Col xs={12} lg={propertySlug ? 12 : { offset: 2, size: 8 }}>
                <LoadingOverlay show={isLoading} inBootstrapColumn={true} />

                {userInfoEntered ? (
                    <div>
                        <SetPasswordWorkflow
                            phoneNumber={userInfo.phoneNumber}
                            onPasswordSubmit={resetPassword}
                            changePhoneNumberEvent={() => setUserInfoEntered(false)}
                            setIsLoading={setIsLoading}
                            setErrorMessage={setErrorMessage}
                            verificationCodeInputLabel={
                                'A code has been sent to the number you ' +
                                'provided. Enter the code below.'
                            }
                            codeConfirmedLabel="Great! Your phone is confirmed."
                            passwordButtonLabel="Next"
                            verificationSectionHeader="Verification Code"
                            passwordSectionHeader="Password"
                            allowPhoneNumberChange={true}
                            propertySlug={propertySlug}
                            persistVerificationCode={false}
                        />
                    </div>
                ) : (
                    <div>
                        <div className="prospect-signup-header">
                            <h4 className="sign-up-text">Sign Up</h4>
                            <span>or</span>
                            <br />
                            <span>
                                <Link
                                    to={
                                        propertySlug
                                            ? tourSignupPath(propertySlug)(TourPage.Login)
                                            : prospectPath(prospectPages.login)
                                    }
                                >
                                    Sign In
                                </Link>
                            </span>
                        </div>
                        <div className="prospect-signup-form">
                            <Form
                                onSubmit={handleSubmitUserInfo}
                                submitButtonText="Next"
                                wasFormValidated={wasFormValidated}
                                onFormValidated={() => setWasFormValidated(true)}
                                inBootstrapColumn={false}
                                buttonGroupClassName="prospect-auth-button-container"
                                additionalSubmitButtonClassname="btn-full-width btn-primary"
                            >
                                <ProspectSignupInputForm
                                    wasFormValidated={wasFormValidated}
                                    userInfo={userInfo}
                                    setValidData={setValidData}
                                    updateUserInfo={(name: string, value: string) =>
                                        setUserInfo({ ...userInfo, [name]: value })
                                    }
                                />
                            </Form>
                            <div className='alignJustify'>
                                <span>
                                    Tour24 is a self-guided app where you can schedule a time to tour a community on your own, without a leasing agent. Tour24 uses your phone as a key to provide temporary access, and to guide you around the community.
                                </span>
                            </div>
                        </div>
                        <div className="registration-terms">
                            <span>
                                <a className='rightMargin' href="/faq">Tour24 FAQs</a> |
                                <a className='leftMargin' href="https://www.youtube.com/watch?v=_UVrQM3reqs">Demo Video</a>
                            </span>
                        </div>
                        <div className="registration-terms">
                            <span>
                                By registering, I accept Tour24's{' '}
                                <a href={termsPdf}>Terms of Use</a>.
                            </span>
                        </div>
                    </div>
                )}

                <FormValidationDisplay errorMessage={errorMessage} />
            </Col>
        </Row>
    );
};

export default RegistrationPage;



// WEBPACK FOOTER //
// ./src/components/pages/prospect/public/RegistrationPage.tsx